import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { ROUTE_REGISTRATION } from "../../Util/VMConstants";
import { toTitleCase } from "../../Util/UtilHelper";

/* ================= HERO SECTION ================= */

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: "url(/static/vm_landing_bg.jpg)",//`url(${vm_landing_bg})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  backgroundPosition: "center",
  padding: theme.spacing(8, 2),

  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    padding: theme.spacing(6, 2),
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 2, 4, 1.5),
    // top | right | bottom | left
  },
}));

/* ================= MAIN CONTAINER ================= */

const HeroContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1400px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(6),

  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    textAlign: "center",
  },
}));

/* ================= CONTENT ================= */

const ContentBlock = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  maxWidth: "650px",

  [theme.breakpoints.down("md")]: {
    alignItems: "center",
    order: 2,
  },

  /* ✅ EXTRA RIGHT PADDING FOR MOBILE */
  [theme.breakpoints.down("sm")]: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1.5),
  },
}));

const HeroTitle = styled(Typography)(() => ({
  fontWeight: 800,
  fontFamily: "Lato, sans-serif",
  textTransform: "uppercase",
  fontSize: "clamp(26px, 4vw, 42px)",
  lineHeight: 1.25,
  color: "#0f172a",
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Lato, sans-serif",
  fontSize: "clamp(16px, 2vw, 22px)",
  lineHeight: 1.7,
  color: "#334155",

  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: 1.6,
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  padding: "14px 40px",
  fontSize: "20px",
  borderRadius: "10px",
  textTransform: "none",
  background: "#1E3A8A",
  color: "#fff",
  width: "fit-content",

  "&:hover": {
    background: "#152d66",
  },

  [theme.breakpoints.down("sm")]: {
    width: "70%",
  },
}));

/* ================= STORE BADGES ================= */

const StoreBadges = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  flexWrap: "wrap",
  marginTop: theme.spacing(1),

  "& img": {
    width: "160px",
    height: "auto",
    cursor: "pointer",
  },

  [theme.breakpoints.down("md")]: {
    justifyContent: "center",
  },
}));

/* ================= IMAGE ================= */

const ComputerImage = styled("img")(({ theme }) => ({
  width: "40%",
  height: "auto",

  [theme.breakpoints.down("md")]: {
    width: "50%",
    order: 1,
  },

  [theme.breakpoints.down("sm")]: {
    width: "55%",
  },
}));

/* ================= COMPONENT ================= */

const VenueManager = () => {
  const navigate = useNavigate();

  return (
    <HeroSection>
      <HeroContainer>
        {/* LEFT CONTENT */}
        <ContentBlock>
          <HeroTitle>
            {toTitleCase("Venue Manager – The CRM Built for Event Venues")}
            
          </HeroTitle>

          <HeroSubtitle>
            {toTitleCase('“ Trusted By Event Venue Owners & Banquet Sales Professionals Across Convention Halls, Banquet Halls, Function Halls, Resorts, Farmhouses, Clubhouses, Wedding lawns & Other Event Venues”')}
            
          </HeroSubtitle>

          <CTAButton
            onClick={() =>
              navigate(`/${ROUTE_REGISTRATION}`, { replace: true })
            }
          >
            Get Started Now
          </CTAButton>

          <StoreBadges>
            <a
              href="https://play.google.com/store/apps/details?id=com.venuemanager.app"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={'/static/google_store.jpg'} alt="Google Play" />
            </a>

            <a
              href="https://apps.apple.com/in/app/venue-manager-banquet-hall-crm/id6737769622"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={'/static/app_store.jpg'} alt="App Store" />
            </a>
          </StoreBadges>
        </ContentBlock>

        {/* RIGHT IMAGE */}
        <ComputerImage
          src={"/static/vm_landing_computer.png"}
          alt="Venue Manager CRM dashboard preview"
        />
      </HeroContainer>
    </HeroSection>
  );
};

export default VenueManager;
